import type { LatLngPoint, User } from '@/shared/types';
import { notificationService } from '@/features/notification/services/notification.service';
import { mapService } from '@/features/map/services/map.service';
import type { FlightNotification } from '../api/flights.api';

function pointToLatLng([lng, lat]: [number, number]): LatLngPoint {
  return { lat, lng };
}

function polygonToLatLngPoints(coords: [number, number][]): LatLngPoint[] {
  // GeoJSON polygons repeat the first vertex as the last to close the ring;
  // the wizard's flightArea list is unclosed.
  return coords.slice(0, -1).map(pointToLatLng);
}

function applyGeometry(flight: FlightNotification): void {
  if (flight.takeoff_point) {
    mapService.setTakeoffPoint(pointToLatLng(flight.takeoff_point.coordinates));
  }
  if (flight.additional_takeoff_points) {
    mapService.setAdditionalTakeoffPoints(flight.additional_takeoff_points.coordinates.map(pointToLatLng));
  }
  if (flight.flight_area) {
    mapService.setFlightArea(polygonToLatLngPoints(flight.flight_area.coordinates[0]));
  }
  mapService.setZoneSettings({
    ...(flight.buffer_zone_meters != null ? { bufferMeters: flight.buffer_zone_meters } : {}),
    ...(flight.termination_zone_meters != null ? { terminationMeters: flight.termination_zone_meters } : {}),
  });
}

// Prefills the notification wizard with a previously-submitted flight's data
// so the pilot can quickly start a new reservation from it — every field
// stays a normal, editable input; nothing is locked or auto-submitted.
export function applyFlightToWizard(flight: FlightNotification, user: User): void {
  notificationService.reset();
  mapService.reset();

  const operator = user.operators.find(o => o.id === String(flight.operator));
  notificationService.updateFormData({
    operatorId: String(flight.operator),
    operatorRegistrationNumber: operator?.registrationNumber ?? '',
    uasId: String(flight.device),
    category: String(flight.category),
    purpose: flight.purpose,
    flightDate: flight.flight_date,
    flightTimeFrom: flight.time_from,
    flightTimeTo: flight.time_to,
    location: flight.location_name,
    plannedAltitude: flight.planned_altitude_m,
    ...(flight.max_distance_m > 0 ? { maxDistance: flight.max_distance_m } : {}),
  });

  // The category change above eventually reaches <DrawingTools>, whose
  // effect tears down and re-subscribes on every category prop change and
  // (intentionally, for the normal "pilot switches category mid-flow" case)
  // calls mapService.reset() as part of that teardown. That happens
  // asynchronously, after this synchronous call returns and React has
  // flushed the effect — so applying geometry synchronously here would get
  // wiped out a moment later. Deferring past that flush is what makes the
  // prefill stick.
  setTimeout(() => applyGeometry(flight), 0);
}
